import { api } from '../lib/api.js';

// ── רכבים ────────────────────────────────────────────────────────────────────
//
// Two surfaces, the same as the server: the office reads and writes the fleet
// behind the staff login, and the driver meets the /kiosk half by scanning the
// QR on the windscreen with a badge number and no account.

export type Vehicle = {
  id: string; plate: string; name: string | null;
  make: string | null; model: string | null; year: number | null;
  /** TRIP — journey by journey. MONTHLY — a car somebody keeps, closed monthly. */
  reporting: 'TRIP' | 'MONTHLY';
  assignedToId: string | null; assignedToName: string | null;
  lastOdometer: number | null; lastOdometerAt: string | null;
  insuranceExpiry: string | null; testExpiry: string | null;
  nextServiceDate: string | null; nextServiceKm: number | null;
  isActive: boolean; notes: string | null;
  /** KM | HOURS — מלגזה בשעות מנוע. */
  meterUnit: 'KM' | 'HOURS'; tankLitres: number | null;
  /** The secret printed into the QR sticker — the kiosk is keyed on it, not on
   *  the plate, which is painted on the outside of the vehicle. */
  qrToken: string;
  /** ביטוח/טסט/טיפול שמתקרב או שעבר. */
  alerts: string[];
  /** מי בחוץ עכשיו ברכב הזה. ריק = אין נסיעה פתוחה. */
  openTrips: { id: string; driverName: string; date: string; leftAt: string | null; odometerOut: number | null; draftAt: string | null }[];
};

/** A journey, from either source — a trip form or an installation day. */
export type LedgerRow = {
  kind: 'TRIP' | 'INSTALLATION';
  id: string; date: string; driverName: string; purpose: string;
  projectId: string | null; projectNumber: string | null; destination: string | null;
  odometerOut: number | null; odometerBack: number | null; km: number | null;
  refuelIls: number | null; refuelLitres: number | null;
  tollsIls: number | null; note: string | null;
  closed: boolean;
  /** מה שנדרש כדי לתקן נסיעה מהמשרד. ריק ביום התקנה — הוא מתוקן ביום עצמו. */
  driverId: string | null; purposeId: string | null;
  /** שעת היציאה והחזרה, כשדווחו בסריקה. */
  leftAt: string | null; returnedAt: string | null;
};

export type VehicleEvent = {
  id: string; kind: string; kindLabel: string; date: string;
  odometer: number | null; description: string; cost: number | null;
  vendor: string | null; validUntil: string | null;
  projectNumber: string | null; byName: string | null;
};

export type VehiclePurpose = { id: string; name: string; nameRu?: string | null; requiresProject: boolean; isActive: boolean };

export type VehicleMonthRow = {
  vehicleId: string; plate: string; name: string | null; driverName: string | null;
  month: string;
  startOdometer: number | null; endOdometer: number | null;
  /** The previous month's closing reading — offered, not imposed. */
  suggestedStart: number | null;
  businessKm: number | null; privateKm: number | null; fuelIls: number | null;
  note: string | null; km: number | null;
  closed: boolean; closedByName: string | null; closedAt: string | null;
  /** הנהג רשם את המונה בסריקת הרכב. */
  reportedByName?: string | null;
};

export type VehicleAlert = { vehicleId: string; plate: string; text: string; dueAt: string | null };

export type VehicleProjectCosts = {
  km: number; trips: number; refuelIls: number; tollsIls: number;
  expenses: { date: string; plate: string; kind: string; description: string; cost: number | null }[];
  expenseTotalIls: number;
  /** הוצאות ברשימה שאין להן סכום — הסיכום חסר אותן. */
  expensesWithoutCost: number;
};

export type VehicleKiosk = {
  vehicle: {
    id: string; plate: string; name: string | null; lastOdometer: number | null;
    reporting: string; insuranceExpiry: string | null; testExpiry: string | null;
    meterUnit: 'KM' | 'HOURS';
  };
  purposes: VehiclePurpose[];
  checks: { before: CheckDef[]; after: CheckDef[] };
  projects: { id: string; number: string; name: string | null }[];
  today: string;
};

/** נסיעה פתוחה כפי שהקיוסק מקבל: כל מה שהטופס צריך כדי לא למחוק את מה שנרשם ביציאה. */
export type KioskTrip = {
  id: string; date: string; purposeId: string | null; projectId: string | null;
  purpose: string | null; projectNumber: string | null; destination: string | null;
  odometerOut: number | null; odometerBack: number | null;
  refuelIls: number | null; refuelLitres: number | null; note: string | null;
  leftAt: string | null; driverName: string;
  purposeRu?: string | null;
  /** מה שהנהג הקליד ועוד לא שמר — נשמר בשרת עד הסגירה (02/11/2026). */
  draft: TripDraft | null; draftAt: string | null;
};

export type CheckDef = { id: string; text: string; textRu?: string | null };

/** שדות הטופס כפי שהוקלדו — מחרוזות, כי טיוטה היא גם מספר באמצע הקלדה. */
export type TripDraft = {
  destination?: string; odometerBack?: string; refuelled?: boolean;
  refuelIls?: string; refuelLitres?: string; note?: string; by?: string;
};

export type KioskMine = {
  driverId: string; driverName: string;
  /** תמיד, לא רק בברכת הבוקר (02/11/2026). */
  photoUrl: string | null; preferredLanguage: string;
  greetToday?: boolean; greeting?: 'MORNING' | 'NOON' | 'EVENING';
  greetEmployee?: { id: string; name: string; photoUrl: string | null; preferredLanguage: string } | null;
  lastOdometer: number | null;
  openTrips: KioskTrip[];
  /** נסיעות פתוחות של נהגים אחרים על אותו רכב: הרכב עובר ידיים. */
  othersOpen: KioskTrip[];
  checksToday: { before: unknown; after: unknown };
  /** כמה עבודות שטח פתוחות יש לנהג — „נוסע להתקנה" מוביל לשם. */
  installJobs: number;
  month: { month: string; reported: boolean; odometer: number | null; by: string | null } | null;
};

export type VehicleChange = { subject: string; action: string; changes: string; reason: string; userName: string; at: string };

export type TripPatch = {
  date: string;
  purposeId?: string | null; projectId?: string | null; destination?: string | null;
  odometerOut?: number | null; odometerBack?: number | null;
  leftAt?: string | null; returnedAt?: string | null;
  refuelIls?: number | null; refuelLitres?: number | null; tollsIls?: number | null;
  note?: string | null;
};

export const vehiclesApi = {
  list: (all = false) => api<Vehicle[]>(`/api/vehicles${all ? '?all=1' : ''}`),
  alerts: () => api<VehicleAlert[]>('/api/vehicles/alerts'),
  save: (id: string | null, body: Partial<Vehicle> & { plate: string; reason?: string }) =>
    id ? api<{ id: string }>(`/api/vehicles/${id}`, { method: 'PATCH', body })
      : api<{ id: string }>('/api/vehicles', { method: 'POST', body }),

  ledger: (id: string, from?: string, to?: string) => {
    const p = new URLSearchParams();
    if (from) p.set('from', from);
    if (to) p.set('to', to);
    return api<LedgerRow[]>(`/api/vehicles/${id}/ledger${p.toString() ? `?${p}` : ''}`);
  },
  events: (id: string) => api<VehicleEvent[]>(`/api/vehicles/${id}/events`),
  addEvent: (body: {
    vehicleId: string; kind: string; date: string; odometer?: number | null;
    description: string; cost?: number | null; vendor?: string | null;
    validUntil?: string | null; projectId?: string | null;
    nextServiceDate?: string | null; nextServiceKm?: number | null;
  }) => api<{ id: string }>('/api/vehicles/events', { method: 'POST', body }),
  deleteEvent: (id: string, reason: string) => api<{ ok: true }>(`/api/vehicles/events/${id}`, { method: 'DELETE', body: { reason } }),
  updateEvent: (id: string, body: {
    date?: string; odometer?: number | null; description?: string; cost?: number | null;
    vendor?: string | null; validUntil?: string | null; reason: string;
  }) => api<{ ok: true }>(`/api/vehicles/events/${id}`, { method: 'PATCH', body }),
  /** תיקון מונה הרכב — בשני הכיוונים, עם סיבה. */
  setOdometer: (id: string, odometer: number, reason: string) =>
    api<{ ok: true }>(`/api/vehicles/${id}/odometer`, { method: 'POST', body: { odometer, reason } }),
  /** בדיקת מונה פיזית, ב-1 לחודש. */
  odometerCheck: (id: string, odometer: number) =>
    api<{ ok: true; last: number | null; gap: number | null }>(`/api/vehicles/${id}/odometer-check`, { method: 'POST', body: { odometer } }),
  changes: (id: string) => api<VehicleChange[]>(`/api/vehicles/${id}/changes`),

  purposes: (all = false) => api<VehiclePurpose[]>(`/api/vehicles/purposes${all ? '?all=1' : ''}`),
  savePurpose: (id: string | null, body: { name?: string; requiresProject?: boolean; isActive?: boolean }) =>
    id ? api<{ ok: true }>(`/api/vehicles/purposes/${id}`, { method: 'PATCH', body })
      : api<{ ok: true }>('/api/vehicles/purposes', { method: 'POST', body }),

  /** תיקון נסיעה מהמשרד. נסיעה שהנהג כבר סגר מחייבת סיבה, והיא נשמרת. */
  saveTrip: (id: string | null, body: TripPatch & { vehicleId: string; driverId: string; reason?: string }) =>
    id ? api<{ id: string }>(`/api/vehicles/trips/${id}`, { method: 'PATCH', body })
      : api<{ id: string }>('/api/vehicles/trips', { method: 'POST', body }),
  closeTrip: (id: string) => api<{ ok: true }>(`/api/vehicles/trips/${id}/close`, { method: 'POST' }),
  deleteTrip: (id: string, reason: string) =>
    api<{ ok: true }>(`/api/vehicles/trips/${id}`, { method: 'DELETE', body: { reason } }),
  tripChanges: (id: string) => api<Array<{
    action: string; changes: string; reason: string; userName: string; at: string;
  }>>(`/api/vehicles/trips/${id}/changes`),

  months: (month: string) => api<VehicleMonthRow[]>(`/api/vehicles/months?month=${encodeURIComponent(month)}`),
  saveMonth: (body: {
    vehicleId: string; month: string;
    startOdometer?: number | null; endOdometer?: number | null;
    businessKm?: number | null; privateKm?: number | null;
    fuelIls?: number | null; note?: string | null; closed?: boolean;
  }) => api<{ ok: true }>('/api/vehicles/months', { method: 'POST', body }),

  projectCosts: (projectId: string) => api<VehicleProjectCosts>(`/api/vehicles/project/${projectId}`),

  /** A fresh sticker — the old one stops working the moment this returns. */
  rotateQrToken: (id: string) => api<{ qrToken: string }>(`/api/vehicles/${id}/qr-token`, { method: 'POST' }),

  // ── הדלת של הנהג ──
  kiosk: (token: string) => api<VehicleKiosk>(`/api/vehicles/kiosk/${encodeURIComponent(token)}`),
  kioskMine: (token: string, employeeNumber: string) =>
    api<KioskMine>(`/api/vehicles/kiosk/${encodeURIComponent(token)}/mine`, { method: 'POST', body: { employeeNumber } }),
  /** מה שהמצלמה פענחה מה-QR של התג. `badge` = האסימון שהמסך שולח מכאן והלאה. */
  kioskIdentify: (token: string, scan: string) =>
    api<KioskMine & { badge: string }>(`/api/vehicles/kiosk/${encodeURIComponent(token)}/identify`, { method: 'POST', body: { scan } }),
  /** הנהג שהטלפון הזה זוכר, או null. */
  kioskMe: (token: string) => api<(KioskMine & { badge: string }) | null>(`/api/vehicles/kiosk/${encodeURIComponent(token)}/me`),
  kioskForget: (token: string) => api<{ ok: true }>(`/api/vehicles/kiosk/${encodeURIComponent(token)}/forget`, { method: 'POST' }),
  kioskDraft: (token: string, body: { employeeNumber: string; tripId: string; draft: Omit<TripDraft, 'by'> }) =>
    api<{ ok: true; at: string }>(`/api/vehicles/kiosk/${encodeURIComponent(token)}/draft`, { method: 'POST', body }),
  kioskMonth: (token: string, employeeNumber: string, odometer: number) =>
    api<{ ok: true; month: string }>(`/api/vehicles/kiosk/${encodeURIComponent(token)}/month`, { method: 'POST', body: { employeeNumber, odometer } }),
  kioskTrip: (token: string, body: TripPatch & {
    employeeNumber: string; tripId?: string | null; close?: boolean;
    onBehalf?: boolean; confirmLowOdometer?: boolean; refuelled?: boolean;
  }) =>
    api<{ id: string }>(`/api/vehicles/kiosk/${encodeURIComponent(token)}/trip`, { method: 'POST', body }),
  kioskCheck: (token: string, body: {
    employeeNumber: string; date: string; phase: 'BEFORE' | 'AFTER';
    odometer?: number | null; note?: string | null;
    items: { text: string; result: 'PASS' | 'FAIL'; note?: string | null }[];
  }) => api<{ ok: true; failures: string[] }>(`/api/vehicles/kiosk/${encodeURIComponent(token)}/check`, { method: 'POST', body }),
};
